import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function SEO({ 
    title = "Milliardi Energo LLC - Mining & Logistics | Environmental Recovery | Oil & Gas Services",
    description = "Leading provider of mining and logistics solutions, environmental recovery, and oil & gas services in Ghana. Transforming environmental challenges into opportunities.",
    keywords = "mining equipment, logistics, environmental recovery, oil gas services, Ghana, environmental remediation, water purification, soil remediation, green hubs, sustainable development, Project EcoRevive, ChistyLOS, activated charcoal, Pra river restoration",
    image = "/images/og-image.jpg",
    url = "https://milliardienergo.com",
    type = "website",
    article = null
}) {
    // Organization structured data
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Milliardi Energo LLC",
        "alternateName": "Milliardi Energo",
        "url": "https://milliardienergo.com",
        "logo": "https://milliardienergo.com/images/Milliardi_logo.png",
        "foundingDate": "2020",
        "industry": "Energy, Mining, Environmental Services",
        "serviceArea": {
            "@type": "Country",
            "name": "Ghana"
        },
        "description": "Leading provider of mining and logistics solutions, environmental recovery, and oil & gas services in Ghana.",
        "address": {
            "@type": "PostalAddress",
            "addressCountry": "Ghana",
            "addressLocality": "Accra"
        },
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+233-XX-XXX-XXXX",
            "contactType": "customer service",
            "email": "enquiries@milliardienergo.com"
        },
        "sameAs": [
            "https://linkedin.com/company/milliardi-energo",
            "https://facebook.com/milliardienergo"
        ]
    };

    // Service structured data
    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Milliardi Energo Services",
        "provider": {
            "@type": "Organization",
            "name": "Milliardi Energo LLC"
        },
        "serviceType": [
            "Mining and Logistics",
            "Oil & Gas Services", 
            "Environmental Recovery",
            "Water Remediation",
            "Soil Restoration"
        ],
        "areaServed": {
            "@type": "Country",
            "name": "Ghana"
        },
        "description": "Comprehensive solutions for mining, oil & gas, and environmental recovery in Ghana."
    };

    // Project structured data
    const projectSchema = {
        "@context": "https://schema.org",
        "@type": "Project",
        "name": "Project EcoRevive",
        "description": "Environmental recovery initiative healing the scars of illegal mining in Ghana's Apamprama forest reserve and surrounding communities.",
        "funder": {
            "@type": "Organization",
            "name": "Milliardi Energo LLC"
        },
        "location": {
            "@type": "Place",
            "name": "Apamprama Forest Reserve, Ghana"
        },
        "startDate": "2024",
        "category": "Environmental Recovery",
        "keywords": "environmental restoration, water purification, soil remediation, community development"
    };

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="robots" content="index, follow" />
            <meta name="author" content="Milliardi Energo LLC" />
            <meta name="language" content="English" />
            <meta name="revisit-after" content="7 days" />
            
            {/* Canonical URL */}
            <link rel="canonical" href={url} />
            
            {/* Favicon */}
            <link rel="icon" href="/images/Milliardi_favicon.png" />
            <link rel="icon" type="image/x-icon" href="/images/Milliardi_favicon.ico" />
            <link rel="shortcut icon" type="image/x-icon" href="/images/Milliardi_favicon.ico" />
            <link rel="apple-touch-icon" href="/images/Milliardi_favicon.png" />
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={`${url}${image}`} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Milliardi Energo - Leading provider of mining, logistics, and environmental recovery services in Ghana" />
            <meta property="og:site_name" content="Milliardi Energo" />
            <meta property="og:locale" content="en_US" />
            
            {/* Twitter Card */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={url} />
            <meta property="twitter:title" content={title} />
            <meta property="twitter:description" content={description} />
            <meta property="twitter:image" content={`${url}${image}`} />
            <meta property="twitter:image:alt" content="Milliardi Energo - Leading provider of mining, logistics, and environmental recovery services in Ghana" />
            
            {/* Article specific meta tags */}
            {article && (
                <>
                    <meta property="article:published_time" content={article.publishedTime} />
                    <meta property="article:modified_time" content={article.modifiedTime} />
                    <meta property="article:author" content={article.author} />
                    <meta property="article:section" content={article.section} />
                    {article.tags && article.tags.map(tag => (
                        <meta key={tag} property="article:tag" content={tag} />
                    ))}
                </>
            )}
            
            {/* Security Headers */}
            <meta http-equiv="X-Content-Type-Options" content="nosniff" />
            <meta http-equiv="X-Frame-Options" content="DENY" />
            <meta http-equiv="X-XSS-Protection" content="1; mode=block" />
            <meta http-equiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
            <meta http-equiv="Permissions-Policy" content="camera=(), microphone=(), geolocation=()" />
            <meta http-equiv="Content-Security-Policy" content="default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self' data:; connect-src 'self' https://www.google-analytics.com https://analytics.google.com;" />
            
            {/* Performance Optimizations */}
            <meta name="format-detection" content="telephone=no" />
            <meta name="msapplication-tap-highlight" content="no" />
            <meta name="theme-color" content="#dc2626" />
            <meta name="msapplication-TileColor" content="#dc2626" />
            
            {/* Preconnect and DNS Prefetch */}
            <link rel="preconnect" href="https://www.google-analytics.com" />
            <link rel="preconnect" href="https://www.googletagmanager.com" />
            <link rel="dns-prefetch" href="//www.google-analytics.com" />
            <link rel="dns-prefetch" href="//www.googletagmanager.com" />
            
            {/* Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(serviceSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(projectSchema)}
            </script>
        </Helmet>
    );
} 